// YNX AI News Feed — sliding panel showing the latest AI updates.
import React, { useEffect, useState, useCallback } from 'react';
import { X, Newspaper, RefreshCw, ExternalLink, Send } from 'lucide-react';
import { fetchAiNews } from '../lib/discovery';

export const AiNewsPanel = ({ open, onClose, onUseAsPrompt }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const list = await fetchAiNews({ force });
      setItems(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load(false);
  }, [open, load]);

  if (!open) return null;

  return (
    <div
      className='modal-overlay active'
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid='ainews-overlay'
    >
      <div className='modal' style={{ maxWidth: 720 }}>
        <div className='modal-header'>
          <div className='modal-title' style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Newspaper size={16} />
            <span>AI NEWS · LIVE FROM THE WORLD</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className='modal-close'
              onClick={() => load(true)}
              disabled={loading}
              title='Rafraîchir le feed'
              data-testid='ainews-refresh'
            >
              <RefreshCw size={14} className={loading ? 'spin' : ''} />
            </button>
            <button className='modal-close' onClick={onClose} data-testid='ainews-close'>
              <X size={14} />
            </button>
          </div>
        </div>

        <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading && items.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: 40, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              <RefreshCw size={20} className='spin' style={{ display: 'block', margin: '0 auto 14px', color: 'var(--cyan)' }} />
              CHARGEMENT DES DERNIÈRES ACTUS IA…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-dim)', padding: 40, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
              Aucune actu disponible. Réessayer dans un instant.
            </div>
          )}

          <ul className='ainews-list' style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {items.map((it, i) => (
              <li
                key={`${it.url}-${i}`}
                style={{
                  padding: '14px 0',
                  borderBottom: i < items.length - 1 ? '1px solid rgba(0,229,255,0.08)' : 'none',
                }}
                data-testid='ainews-item'
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--cyan)', letterSpacing: 1, textTransform: 'uppercase' }}>
                    {it.domain || 'web'}
                  </span>
                  <a
                    href={it.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ color: 'var(--text-dim)', fontSize: 10 }}
                    title='Ouvrir la source'
                  >
                    <ExternalLink size={11} />
                  </a>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-bright)', lineHeight: 1.4, marginBottom: 6, fontWeight: 600 }}>
                  {it.title}
                </div>
                {it.snippet && (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5, marginBottom: 8 }}>
                    {it.snippet}
                  </div>
                )}
                <button
                  className='hud-btn'
                  style={{ padding: '4px 10px', fontSize: 10 }}
                  onClick={() => { onUseAsPrompt?.(it); onClose(); }}
                  data-testid='ainews-use-as-prompt'
                >
                  <Send size={11} /> ANALYSE AVEC YNX
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
